<template>
  <div class="campaigns-reports-container">
    <CampaignsFilters
      ref="filters"
      :campaignsData="campaigns"
      @filter-change="onFilterChange"
    />
    <div class="reports-wrap">
      <CampaignsSummary :messages="filteredCampaigns" />
      <div class="bar-section-group">
        <div class="bar-section">
          <h5 class="chart-title">
            {{ $t('CAMPAIGNS_REPORT.TABLE.MESSAGE_STATUS') }}
          </h5>
          <StatusMessageDoughnutChart :messages="filteredCampaigns" />
        </div>

        <div class="bar-section">
          <h5 class="chart-title">
            {{ $t('CAMPAIGNS_REPORT.TABLE.HAS_INTERACTION') }}
          </h5>
          <InteractionDoughnutChart :messages="filteredCampaigns" />
        </div>
      </div>
      <div class="table-section no-bg">
        <CampaignsTable :campaignsData="filteredCampaigns" />
      </div>
    </div>
  </div>
</template>

<script>
import CampaignsTable from './components/CampaignsTable';
import CampaignsFilters from './components/CampaignsFilters';
import CampaignsReportApi from '../../../../api/inbox/historyCampaigns';
import StatusMessageDoughnutChart from './components/StatusMessageDoughnutChart.vue';
import InteractionDoughnutChart from './components/InteractionDoughnutChart.vue';
import CampaignsSummary from './components/CampaignsSummary';

export default {
  name: 'CampaignsReportsView',
  components: {
    CampaignsFilters,
    CampaignsTable,
    StatusMessageDoughnutChart,
    InteractionDoughnutChart,
    CampaignsSummary,
  },
  data() {
    return {
      campaigns: [],
      filters: {
        customDateRange: '',
        inboxNames: [],
        campaignTitles: [],
        statusMessages: [],
        errorMessages: [],
        interactions: [],
      },
    };
  },

  computed: {
    filteredCampaigns() {
      const [start, end] = this.filters.customDateRange || [];

      return this.campaigns.filter(item => {
        const createdDate = new Date(item.created_at);
        const createdDateOnly = new Date(
          createdDate.getFullYear(),
          createdDate.getMonth(),
          createdDate.getDate()
        );

        const startDate = start ? new Date(start) : null;
        const endDate = end ? new Date(end) : null;

        const matchDate =
          !startDate ||
          !endDate ||
          (createdDateOnly >= startDate && createdDateOnly <= endDate);

        const matchInbox =
          !this.filters.inboxNames.length ||
          this.filters.inboxNames.includes(item.inbox_name);

        const matchCampaign =
          !this.filters.campaignTitles.length ||
          this.filters.campaignTitles.includes(item.campaign_title);

        const matchStatus =
          !this.filters.statusMessages.length ||
          this.filters.statusMessages.includes(item.status_message);

        const matchError =
          !this.filters.errorMessages.length ||
          this.filters.errorMessages.includes(item.message_error);

        const matchInteraction =
          !this.filters.interactions.length ||
          this.filters.interactions.includes(item.interacted);

        return (
          matchDate &&
          matchInbox &&
          matchCampaign &&
          matchStatus &&
          matchError &&
          matchInteraction
        );
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.filters.emitFilters();
    });
  },
  methods: {
    prepareCampaigns(rawCampaigns) {
      if (!Array.isArray(rawCampaigns)) return [];

      return rawCampaigns.map(campaign => {
        const phone = campaign.phone_number;

        let formattedNumber = phone;
        if (phone?.startsWith('+55') && phone.length === 13) {
          const ddd = phone.slice(3, 5);
          const part1 = phone.slice(5, 6);
          const part2 = phone.slice(6, 10);
          const part3 = phone.slice(10);
          formattedNumber = `(${ddd}) ${part1} ${part2}-${part3}`;
        }

        const statusMap = {
          sent: '1 check do WhatsApp',
          delivered: '2 checks do WhatsApp',
          read: '2 checks verdes do WhatsApp',
          failed: 'Falha no envio',
        };

        const status_message = statusMap[campaign.status] || 'Desconhecido';

        const message_error =
          campaign.content_attributes?.external_error || '-';
        const interacted =
          new Date(campaign.last_activity_at) >= new Date(campaign.created_at)
            ? 'SIM'
            : 'NÃO';

        const link_conversation = `${window.location.origin}/app/accounts/${campaign.account_id}/conversations/${campaign.display_id}?messageId=${campaign.message_id}`;

        return {
          ...campaign,
          formatted_number: formattedNumber,
          status_message,
          message_error,
          interacted,
          link_conversation,
        };
      });
    },
    async onFilterChange(newFilters) {
      this.filters = {
        ...this.filters,
        ...newFilters,
      };

      try {
        const [start, end] = this.filters.customDateRange || [];
        const formatDate = date => new Date(date).toISOString().split('T')[0];

        const response = await CampaignsReportApi.getAll({
          startDate: start ? formatDate(start) : null,
          endDate: end ? formatDate(end) : null,
        });

        const campaigns = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];

        this.campaigns = this.prepareCampaigns(campaigns);
      } catch (error) {
        console.error(this.$t('CAMPAIGNS_REPORT.ERRORS.FETCH'), error);
      }
    },
  },
};
</script>

<style scoped>
.campaigns-reports-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  align-items: flex-start;
}

.campaigns-reports-container > *:first-child {
  width: 280px;
  flex-shrink: 0;
  margin-right: 24px;
}

.reports-wrap {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 2vh 2vw 10vh;
  background: #f8fafc;
  overflow: hidden;
  min-width: 0;
  box-sizing: border-box;
}

.bar-section,
.table-section {
  margin-top: 4vh;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  width: 100%;
  overflow-x: auto;
}

.table-section.no-bg {
  background: transparent;
  padding: 0;
  box-shadow: none;
}

.bar-section-group {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 1vh;
}

.bar-section-group .bar-section {
  flex: 1 1 48%;
  max-height: 300px;
  min-height: 280px;
  overflow: hidden;
  background: #fff;
  padding: 1rem;
  border-radius: 10px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.05);
}

.chart-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 12px;
}
</style>
